import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Eye } from 'lucide-react';
import { tableLabel } from '../../lib/tableLabel';

export default function HardDeletePage() {
  const [orders, setOrders] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [hiddenLogs, setHiddenLogs] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, logsRes] = await Promise.all([
        api.get('/orders'),
        api.get('/hidden-audit-logs').catch(() => ({ data: [] }))
      ]);
      setOrders(ordersRes.data);
      setHiddenLogs(logsRes.data);
    } catch (err) { console.error('[HardDeletePage.js] error:', err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hardDelete = async () => {
    if (confirmText !== 'PERMANENTLY DELETE') { toast.error('Type "PERMANENTLY DELETE" to confirm'); return; }
    if (!reason) { toast.error('Reason required'); return; }
    try {
      await api.post('/orders/hard-delete', { order_id: deleteDialog, confirm_text: confirmText, reason });
      toast.success('Order permanently deleted');
      setDeleteDialog(null); setConfirmText(''); setReason('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 font-sans text-xs uppercase tracking-wider font-bold">Danger Zone - Permanent deletion cannot be undone</span>
        </div>
      </div>

      <div className="space-y-2">
        {orders.slice(0, 50).map(order => (
          <div key={order.id} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`hard-delete-order-${order.id}`}>
            <div>
              <span className="text-white font-sans text-sm">#{order.id.slice(0, 8)}</span>
              <span className="text-zinc-500 text-xs font-sans ml-2">{tableLabel(order.table_number)} - ${order.total?.toFixed(2)}</span>
              <Badge className={`ml-2 ${order.order_status === 'voided' ? 'bg-red-500/20 text-red-400' : order.order_status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>{order.order_status}</Badge>
            </div>
            <Dialog open={deleteDialog === order.id} onOpenChange={open => { if (!open) setDeleteDialog(null); }}>
              <Button size="sm" variant="ghost" onClick={() => setDeleteDialog(order.id)} className="h-7 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm font-sans text-xs" data-testid={`hard-delete-btn-${order.id}`}>
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
              <DialogContent className="bg-bg-secondary border border-red-500/20">
                <DialogHeader><DialogTitle className="font-serif text-red-400">Permanently Delete Order</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <p className="text-zinc-400 font-sans text-sm">This action is irreversible. Order data will be stored in a hidden audit log.</p>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Reason</Label>
                    <Input value={reason} onChange={e => setReason(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Why delete?" data-testid="hard-delete-reason" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-[0.15em] text-red-400 font-sans">Type "PERMANENTLY DELETE" to confirm</Label>
                    <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} className="bg-transparent border border-red-500/20 text-red-400 font-sans" data-testid="hard-delete-confirm-input" />
                  </div>
                  <Button onClick={hardDelete} disabled={confirmText !== 'PERMANENTLY DELETE'} className="w-full bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 rounded-sm font-sans disabled:opacity-30" data-testid="confirm-hard-delete-button">Permanently Delete</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ))}
      </div>

      {hiddenLogs.length > 0 && (
        <div>
          <h3 className="text-sm font-sans text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2"><Eye className="w-3 h-3" /> Hidden Audit Log ({hiddenLogs.length})</h3>
          <div className="space-y-2">
            {hiddenLogs.map(log => (
              <div key={log.id} className="p-3 bg-red-500/5 border border-red-500/10 rounded-sm" data-testid={`hidden-log-${log.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-red-400 text-xs font-sans font-bold">{log.action}</span>
                    <span className="text-zinc-500 text-xs font-sans ml-2">by {log.deleter_name}</span>
                  </div>
                  <span className="text-zinc-600 text-xs font-mono">{new Date(log.created_at).toLocaleString()}</span>
                </div>
                <p className="text-zinc-400 text-xs font-sans mt-1">Reason: {log.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
